/**
 * Protocolo do ditado ao vivo (spec 009, fase 5) — a parte PURA.
 *
 * Aqui não tem WebSocket, microfone nem React: só a tradução entre o que o
 * Gemini Live fala (JSON do `BidiGenerateContent`) e o que a fila já entende
 * (`AIStructuredResult` → `resolveVoiceDrafts`). Separar assim é o que deixa
 * o protocolo testável sem abrir sessão nenhuma.
 *
 * O modelo NÃO conversa: ele escuta e, a cada pedido, chama a função
 * `preparar_documentos` com o MESMO JSON que a extração por gravação devolve.
 * Daí pra frente o caminho é o de sempre — parser determinístico, catálogo,
 * ancoragem na transcrição, fila de conferência. Nada imprime sozinho: só
 * quando o médico manda ("imprime…" → `imprimir_documentos`), com o verbo
 * conferido na transcrição do serviço e só card sem pendência (03/10/2026).
 *
 * 05/10/2026 — o médico manda no próprio ditado (pedido do Leopoldo: "parar,
 * pausar e salvar como instruções do ditado ao vivo pro modelo"): "pausa" →
 * `pausar_ditado`, "pode parar" → `encerrar_ditado`, "salva no prontuário" →
 * `registrar_no_prontuario`. Mesma trava do imprimir: o verbo tem de estar
 * na transcrição do serviço.
 */

import type { AIStructuredResult } from '@/types/ai';
import type { VoiceDraft } from './resolveDrafts';
import { examListToFill } from './examFill';

/** Nome da função que o modelo chama pra entregar documentos. */
export const LIVE_TOOL_PREPARAR = 'preparar_documentos';
/** "Tira a receita de óculos" — remove da fila o último card de um tipo. */
export const LIVE_TOOL_REMOVER = 'remover_documento';
/** "Imprime tudo" / "imprime os óculos" — imprime o que está completo (ver `livePrint.ts`). */
export const LIVE_TOOL_IMPRIMIR = 'imprimir_documentos';
/** "Pausa" — o ditado para de ouvir sem fechar (volta com Retomar, "Jarvis" ou "Atlas, continua"). */
export const LIVE_TOOL_PAUSAR = 'pausar_ditado';
/** "Pode parar" / "desliga" — encerra o ditado ao vivo. */
export const LIVE_TOOL_ENCERRAR = 'encerrar_ditado';
/** "Salva no prontuário" — o mesmo botão "Registrar no prontuário" da coluna. */
export const LIVE_TOOL_REGISTRAR = 'registrar_no_prontuario';

/** O médico mandando no próprio ditado. */
export type LiveControl = 'pausar' | 'encerrar' | 'registrar';
export const LIVE_CONTROL_TOOL: Record<LiveControl, string> = {
  pausar: LIVE_TOOL_PAUSAR,
  encerrar: LIVE_TOOL_ENCERRAR,
  registrar: LIVE_TOOL_REGISTRAR,
};
const CONTROLE_DA_FUNCAO = new Map<string, LiveControl>(
  (Object.entries(LIVE_CONTROL_TOOL) as Array<[LiveControl, string]>).map(([acao, nome]) => [nome, acao]),
);

/**
 * O que o médico pode mandar imprimir por voz. "solicitacaoExames" e
 * "solicitacaoCirurgia" são as PARTES do card "Solicitações": o pedido de
 * exames (com o retorno) e o de cirurgia. "atestadoLaudo" = os atestados,
 * declarações e laudos (08/10/2026).
 */
export type PrintTarget =
  | 'tudo'
  | 'receitaOculos'
  | 'receitaMedicamentos'
  | 'solicitacaoExames'
  | 'solicitacaoCirurgia'
  | 'atestadoLaudo';
export const PRINT_TARGETS: PrintTarget[] = [
  'tudo',
  'receitaOculos',
  'receitaMedicamentos',
  'solicitacaoExames',
  'solicitacaoCirurgia',
  'atestadoLaudo',
];

/** Taxa que o Live espera na entrada. O microfone é reamostrado pra isto. */
export const LIVE_INPUT_SAMPLE_RATE = 16_000;
export const LIVE_INPUT_MIME = `audio/pcm;rate=${LIVE_INPUT_SAMPLE_RATE}`;

/** Tipos que o médico pode mandar tirar por voz. "solicitacaoCirurgia" tira só a parte de cirurgia do card de solicitações. */
export type RemovableKind = VoiceDraft['kind'] | 'solicitacaoCirurgia';
const KINDS: RemovableKind[] = [
  'receitaOculos',
  'receitaMedicamentos',
  'solicitacaoExames',
  'solicitacaoCirurgia',
  'examesAtendimento',
  'atestadoLaudo',
];

const eyeSchema = {
  type: 'OBJECT',
  properties: {
    esferica: {
      type: 'STRING',
      description:
        'Com o sinal DITO, ex.: "+1.00", "-0.75"; "plano" se dito plano. Dito sem "mais"/"menos": o número sem sinal ' +
        '(nunca adivinhe). Vazio se não dito.',
    },
    cilindrica: { type: 'STRING', description: 'Com o sinal DITO, ex.: "-1.25". Vazio se não dito.' },
    eixo: { type: 'STRING', description: 'Graus, ex.: "180". Vazio se não dito.' },
    adicao: {
      type: 'STRING',
      description:
        'Ex.: "+2.00". Adição dita SEM nomear o olho ("adição de 2,00", "adição dois para perto") ' +
        'vale para os DOIS olhos: preencha o mesmo valor em od.adicao e oe.adicao. ' +
        'Só fica em um olho quando o médico nomear o olho junto da adição ("adição só no OD"). Vazio se não dito.',
    },
    dp: { type: 'STRING' },
    av: { type: 'STRING' },
  },
};

/**
 * Declaração das ferramentas, no formato `functionDeclarations` do Live.
 *
 * O schema espelha `AIStructuredResult` só nos alvos do ditado — não existe
 * evolução aqui, então o modelo nem tem onde colocar. Atestado/laudo
 * (08/10/2026) é só o TÍTULO de um modelo da clínica: o texto nunca vem da IA.
 */
export function liveToolDeclarations() {
  return [
    {
      functionDeclarations: [
        {
          name: LIVE_TOOL_PREPARAR,
          description:
            'Entrega o que o médico acabou de pedir ou registrar para este paciente: pedido ditado, conduta que ele ' +
            'anunciou que vai fazer agora ("vou pedir…", "vou te passar…"), receita, retorno, medida ou achado do exame ' +
            'de hoje. Chame UMA vez por pedido, assim que ele terminar. Conversa sem pedido, conduta nem achado não gera ' +
            'chamada. Só preencha o que foi realmente dito; o resto fica vazio.',
          parameters: {
            type: 'OBJECT',
            properties: {
              transcricao: {
                type: 'STRING',
                description: 'O que o médico disse NESTE pedido, literal, palavra por palavra, sem corrigir nem completar.',
              },
              medicamentos: {
                type: 'ARRAY',
                items: {
                  type: 'OBJECT',
                  properties: {
                    nome: { type: 'STRING', description: 'Nome EXATO do bloco "### Banco de medicamentos" quando algum bater com o que foi ouvido.' },
                    dosagem: { type: 'STRING' },
                    posologia: { type: 'STRING', description: 'Só se ditada. Vazio usa a do banco.' },
                    duracao: { type: 'STRING' },
                    via: { type: 'STRING' },
                    quantidade: { type: 'STRING' },
                    olho: { type: 'STRING', description: '"OD", "OE", "AO" ou vazio.' },
                  },
                  required: ['nome'],
                },
              },
              exames: {
                type: 'ARRAY',
                items: {
                  type: 'OBJECT',
                  properties: {
                    nome: { type: 'STRING' },
                    justificativa: { type: 'STRING', description: 'SEMPRE vazio: quem justifica é o médico.' },
                    cid: { type: 'STRING', description: 'SEMPRE vazio.' },
                  },
                  required: ['nome'],
                },
              },
              examesAtendimento: {
                type: 'ARRAY',
                description:
                  'RESULTADOS do exame de hoje: medida com o valor ("pressão 14 no direito e 16 no esquerdo", ' +
                  '"acuidade 20/20 nos dois olhos") ou ACHADO descrito, mesmo sem o nome do exame ("córnea transparente", ' +
                  '"pterígio nasal no olho direito", "retina aplicada", "fundo de olho sem alterações"). Um item por valor; ' +
                  'achados do mesmo módulo e olho juntos num item. Use as chaves do bloco "### Módulos de exame disponíveis". ' +
                  'PEDIDO de exame vai em "exames", não aqui. Comentário sem valor nem achado ("a pressão está boa") não ' +
                  'entra. Vazio se não dito.',
                items: {
                  type: 'OBJECT',
                  properties: {
                    modulo: { type: 'STRING', description: 'Id do módulo, ex.: "pio".' },
                    campo: { type: 'STRING', description: 'Chave do campo do módulo.' },
                    olho: {
                      type: 'STRING',
                      enum: ['OD', 'OE', 'AO', 'mono'],
                      description:
                        '"AO" = mesmo valor nos dois olhos ("nos dois olhos", "ambos"). "mono" = campo de valor único. ' +
                        'Achado em texto (biomicroscopia, fundoscopia, mapeamento de retina) dito sem olho = "AO".',
                    },
                    valor: {
                      type: 'STRING',
                      description:
                        'Medida: exatamente como dita (números em algarismos), sem unidade. Achado: só o achado, com as ' +
                        'palavras do médico ("pterígio nasal", não "o senhor tem um pterígio, viu?").',
                    },
                  },
                  required: ['modulo', 'campo', 'olho', 'valor'],
                },
              },
              receitaOculos: {
                type: 'OBJECT',
                nullable: true,
                properties: {
                  od: eyeSchema,
                  oe: eyeSchema,
                  observacoes: {
                    type: 'STRING',
                    description: 'Tipo de lente e tratamentos ditados ("lentes progressivas", "antirreflexo"), separados por ";". Vazio se não dito.',
                  },
                },
              },
              cirurgias: {
                type: 'STRING',
                description:
                  'Uma cirurgia/procedimento por linha, com o olho, ex.: "Exérese de pterígio OD". Duas cirurgias NUNCA ' +
                  'na mesma linha (viram documentos separados). Vazio se nenhuma.',
              },
              receitaObservacoes: {
                type: 'STRING',
                description:
                  'Orientações gerais da receita de medicamentos, com as palavras do médico: "não suspender o colírio ' +
                  'sem recomendação", "tomar após as refeições", "não dirigir", "guardar na geladeira", "intervalo de ' +
                  '5 minutos entre os colírios". Tudo sobre COMO usar a receita que não seja a posologia de UM remédio. ' +
                  'Pode vir sozinho, sem "medicamentos": orientação mandada escrever na receita que já está na fila. ' +
                  'Título de modelo de orientação da clínica dito → o título exato (vários separados por ";"). ' +
                  'Vazio se não dito.',
              },
              examesObservacoes: {
                type: 'STRING',
                description: 'Orientações do pedido de exames ditadas ("agendar os exames na recepção", "trazer os exames"). Vazio se não dito.',
              },
              cirurgiaObservacoes: {
                type: 'STRING',
                description: 'Observações/justificativa ditadas da solicitação de cirurgia ("contraindicação", "com anestesia geral"). Vazio se não dito.',
              },
              separado: {
                type: 'BOOLEAN',
                description:
                  'true SÓ se o médico pediu explicitamente documento/PDF/card SEPARADO ("em PDF separado", ' +
                  '"card independente", "solicitação separada da anterior"). A frase de comando não é conteúdo do ' +
                  'documento. Caso contrário false.',
              },
              retorno: {
                type: 'OBJECT',
                nullable: true,
                description:
                  'Próxima avaliação pedida: "retorno para mostrar exames até dezembro de 2026", ' +
                  '"nova consulta em março". Null se o médico não pediu retorno.',
                properties: {
                  motivo: { type: 'STRING', description: 'Como foi dito, ex.: "Retorno para mostrar exames".' },
                  mes: { type: 'STRING', description: 'Mês no formato YYYY-MM. "em 3 meses" conta a partir de hoje (data no prompt).' },
                },
                required: ['motivo', 'mes'],
              },
              atestadosLaudos: {
                type: 'ARRAY',
                description:
                  'Atestado, declaração, laudo ou outro documento de TEXTO da lista "### Modelos de documento" que o ' +
                  'médico mandou fazer ou anunciou que vai dar ao paciente. Um item por documento. Vazio se não pedido.',
                items: {
                  type: 'OBJECT',
                  properties: {
                    modelo: {
                      type: 'STRING',
                      description:
                        'Título EXATO de um modelo da lista "### Modelos de documento". Sem modelo que corresponda ao ' +
                        'pedido (ou na dúvida entre dois): o pedido como foi dito.',
                    },
                    pedido: { type: 'STRING', description: 'O pedido como foi dito, ex.: "atestado de três dias".' },
                    dias: {
                      type: 'STRING',
                      description: 'Dias de afastamento/repouso, em algarismos, SÓ se ditos ("por três dias" → "3"). Vazio se não ditos.',
                    },
                  },
                  required: ['modelo'],
                },
              },
            },
            required: ['transcricao'],
          },
        },
        {
          name: LIVE_TOOL_REMOVER,
          description:
            'O médico MANDOU tirar/cancelar/apagar algo que já está na fila ("tira a receita de óculos", ' +
            '"cancela a retinografia", "tira o Vigamox", "cancela o atestado"). "Não precisa de óculos" é conversa, ' +
            'não remoção. "examesAtendimento" = os valores de exame ditados; "atestadoLaudo" = atestado, declaração ' +
            'ou laudo.',
          parameters: {
            type: 'OBJECT',
            properties: {
              kind: { type: 'STRING', enum: KINDS },
              item: {
                type: 'STRING',
                description:
                  'Só quando ele citou UM item: o medicamento, o exame pedido, a cirurgia, o exame medido ("pressão") ' +
                  'ou o atestado/laudo ("a declaração de comparecimento") a tirar, como está na fila. Vazio = o ' +
                  'documento inteiro.',
              },
            },
            required: ['kind'],
          },
        },
        {
          name: LIVE_TOOL_IMPRIMIR,
          description:
            'O médico MANDOU imprimir ("imprime tudo", "pode imprimir os óculos", "imprime a receita e o pedido de ' +
            'exames", "vou imprimir sua receita"). Imprimir não é pedido novo: não chame "preparar_documentos" por ' +
            'causa disso. "tudo" = todos os documentos prontos; "receitaOculos" = óculos/grau; "receitaMedicamentos" = ' +
            '"a receita" sem dizer óculos, colírio, remédio; "solicitacaoExames" = pedido de exames; ' +
            '"solicitacaoCirurgia" = cirurgia/procedimento/laser; "atestadoLaudo" = atestado, declaração, laudo. ' +
            'O sistema só imprime o que está completo.',
          parameters: {
            type: 'OBJECT',
            properties: {
              alvos: {
                type: 'ARRAY',
                items: { type: 'STRING', enum: PRINT_TARGETS },
                description: 'O que imprimir. "Imprime" ou "pode imprimir" sem dizer o quê = ["tudo"].',
              },
            },
            required: ['alvos'],
          },
        },
        {
          name: LIVE_TOOL_PAUSAR,
          description:
            'O médico MANDOU pausar o ditado ("pausa", "pause", "pausa aí", "Atlas, pause"). Não é pausar remédio ' +
            'ou tratamento ("pausa o corticoide por uma semana").',
        },
        {
          name: LIVE_TOOL_ENCERRAR,
          description:
            'O médico MANDOU parar, desligar ou encerrar o DITADO ("pare", "pode parar", "para o ditado", "desliga", ' +
            '"encerra o ditado", "Atlas, pare"). Não é parar remédio ("pare o colírio", "para de usar a lágrima") nem ' +
            'falar com o paciente ("pare de coçar o olho", "para de olhar pra luz").',
        },
        {
          name: LIVE_TOOL_REGISTRAR,
          description:
            'O médico MANDOU salvar no prontuário o que está preparado ("salva", "salve", "pode salvar", "registra no ' +
            'prontuário", "Atlas, salve"). Salvar não é pedido novo: não chame "preparar_documentos" por causa disso.',
        },
      ],
    },
  ];
}

/** Mensagem `setup` — a primeira que o navegador manda depois de conectar. */
export function buildSetupMessage(model: string, systemInstruction: string) {
  return {
    setup: {
      model: model.startsWith('models/') ? model : `models/${model}`,
      // O 3.8 Live só gera ÁUDIO (pedir TEXT é recusado pelo modelo). O que
      // ele falar é descartado em `parseServerMessage` — o prompt manda calar.
      generationConfig: { responseModalities: ['AUDIO'] },
      systemInstruction: { parts: [{ text: `O idioma falado é SEMPRE português do Brasil (pt-BR), em contexto de consulta oftalmológica. Nunca interprete a fala como outro idioma.

${systemInstruction}` }] },
      tools: liveToolDeclarations(),
      // A transcrição vem do próprio serviço, não do modelo: é nela que a
      // ancoragem confere se o documento foi mesmo ditado.
      // Idioma FIXO: sem ele o serviço detecta sozinho e já transcreveu
      // consulta inteira em espanhol (02/10/2026). O token é carimbado com o
      // mesmo valor no servidor (`/live-token`).
      inputAudioTranscription: { languageCodes: ['pt-BR'] },
    },
  };
}

export function buildAudioMessage(base64Pcm: string) {
  return { realtimeInput: { audio: { mimeType: LIVE_INPUT_MIME, data: base64Pcm } } };
}

/**
 * Resposta à chamada de função. Além do `ok`, pode levar o estado da fila
 * (`fila`/`pendencias`, ver `summarizeVoiceQueueForModel`) — é o único jeito
 * do modelo saber o que já entrou, o que a remoção tirou e o que não passou
 * na conferência, sem depender da memória da conversa.
 */
export function buildToolResponse(id: string, name: string, ok = true, extra?: Record<string, unknown>) {
  return { toolResponse: { functionResponses: [{ id, name, response: { ok, ...(extra ?? {}) } }] } };
}

/** O que sai de uma mensagem do servidor, já mastigado pro modal. */
export type LiveEvent =
  | { type: 'ready' }
  | { type: 'transcript'; text: string }
  | { type: 'turnComplete' }
  | { type: 'prepare'; id: string; structured: AIStructuredResult; transcricao: string }
  | { type: 'remove'; id: string; kind: RemovableKind; item: string }
  | { type: 'print'; id: string; alvos: PrintTarget[] }
  | { type: 'control'; id: string; acao: LiveControl }
  | { type: 'unknownTool'; id: string; name: string }
  | { type: 'ignore' };

interface FunctionCall { id?: string; name?: string; args?: Record<string, unknown> }

/**
 * Args da função → `AIStructuredResult`, no shape que `resolveVoiceDrafts` já
 * lê. Campo ausente vira vazio; nada aqui decide validade — isso é do parser.
 */
export function toolArgsToStructured(
  args: Record<string, unknown> | undefined,
): AIStructuredResult & { transcricao: string } {
  const a = args ?? {};
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const meds = Array.isArray(a.medicamentos) ? a.medicamentos : [];
  const exams = Array.isArray(a.exames) ? a.exames : [];
  const lens = a.receitaOculos && typeof a.receitaOculos === 'object'
    ? (a.receitaOculos as Record<string, Record<string, unknown> | string | undefined>)
    : null;
  const eye = (e: unknown) => {
    const o = (e && typeof e === 'object' ? e : {}) as Record<string, unknown>;
    return {
      esferica: str(o.esferica),
      cilindrica: str(o.cilindrica),
      eixo: str(o.eixo),
      adicao: str(o.adicao),
      dp: str(o.dp),
      av: str(o.av),
    };
  };
  return {
    transcricao: str(a.transcricao),
    evolucao: {
      queixaPrincipal: '', historiaAtual: '', exameFisico: '', hipotesesDiagnosticas: [], cid: '', conduta: '',
    },
    documentos: [],
    medicamentos: meds
      .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object')
      .map((m) => ({
        nome: str(m.nome),
        dosagem: str(m.dosagem),
        posologia: str(m.posologia),
        duracao: str(m.duracao),
        via: str(m.via),
        quantidade: str(m.quantidade),
        olho: (['OD', 'OE', 'AO'].includes(str(m.olho)) ? str(m.olho) : '') as 'OD' | 'OE' | 'AO' | '',
      })),
    exames: exams
      .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
      .map((e) => ({ nome: str(e.nome), justificativa: '', cid: '' })),
    receitaOculos: lens ? { od: eye(lens.od), oe: eye(lens.oe), observacoes: str(lens.observacoes) } : null,
    cirurgias: str(a.cirurgias),
    // Lista da função → mapa da extração; a validação contra o catálogo é
    // do `resolveVoiceDrafts`, igual ao push-to-talk.
    examesAtendimento: examListToFill(a.examesAtendimento),
    separado: a.separado === true,
    receitaObservacoes: str(a.receitaObservacoes),
    examesObservacoes: str(a.examesObservacoes),
    cirurgiaObservacoes: str(a.cirurgiaObservacoes),
    retorno: a.retorno && typeof a.retorno === 'object'
      ? {
          motivo: str((a.retorno as Record<string, unknown>).motivo),
          mes: str((a.retorno as Record<string, unknown>).mes),
        }
      : null,
    atestadosLaudos: (Array.isArray(a.atestadosLaudos) ? a.atestadosLaudos : [])
      .filter((d): d is Record<string, unknown> => !!d && typeof d === 'object')
      .map((d) => ({ modelo: str(d.modelo), pedido: str(d.pedido), dias: typeof d.dias === 'number' ? String(d.dias) : str(d.dias) }))
      .filter((d) => d.modelo.trim() || d.pedido.trim()),
  };
}

/**
 * Uma mensagem do servidor → zero ou mais eventos.
 *
 * Tudo que não interessa (texto solto do modelo, metadados de uso) vira
 * `ignore` — o modal nunca mostra "resposta" do modelo, só transcrição e
 * cards.
 */
export function parseServerMessage(raw: unknown): LiveEvent[] {
  const msg = (raw ?? {}) as Record<string, any>;
  const out: LiveEvent[] = [];
  if (msg.setupComplete) out.push({ type: 'ready' });
  const sc = msg.serverContent;
  if (sc) {
    const t = sc.inputTranscription?.text;
    if (typeof t === 'string' && t) out.push({ type: 'transcript', text: t });
    if (sc.turnComplete) out.push({ type: 'turnComplete' });
  }
  const calls: FunctionCall[] = Array.isArray(msg.toolCall?.functionCalls) ? msg.toolCall.functionCalls : [];
  for (const call of calls) {
    const id = String(call.id ?? '');
    if (call.name === LIVE_TOOL_PREPARAR) {
      const structured = toolArgsToStructured(call.args);
      out.push({ type: 'prepare', id, structured, transcricao: structured.transcricao });
    } else if (call.name === LIVE_TOOL_REMOVER) {
      const kind = String(call.args?.kind ?? '') as RemovableKind;
      const item = typeof call.args?.item === 'string' ? call.args.item.trim() : '';
      if (KINDS.includes(kind)) out.push({ type: 'remove', id, kind, item });
      else out.push({ type: 'unknownTool', id, name: LIVE_TOOL_REMOVER });
    } else if (call.name === LIVE_TOOL_IMPRIMIR) {
      const lista = Array.isArray(call.args?.alvos) ? call.args.alvos : [];
      const validos = [...new Set(lista.filter((a: unknown): a is PrintTarget =>
        typeof a === 'string' && (PRINT_TARGETS as string[]).includes(a)))];
      // "Tudo" já cobre os outros: imprimir duas vezes o mesmo card não é o pedido.
      const alvos: PrintTarget[] = validos.includes('tudo') ? ['tudo'] : validos;
      if (alvos.length > 0) out.push({ type: 'print', id, alvos });
      else out.push({ type: 'unknownTool', id, name: LIVE_TOOL_IMPRIMIR });
    } else if (CONTROLE_DA_FUNCAO.has(String(call.name ?? ''))) {
      out.push({ type: 'control', id, acao: CONTROLE_DA_FUNCAO.get(String(call.name))! });
    } else {
      out.push({ type: 'unknownTool', id, name: String(call.name ?? '') });
    }
  }
  return out.length > 0 ? out : [{ type: 'ignore' }];
}

/** Float32 (-1..1) → PCM 16 bits little-endian, base64. */
export function floatToPcm16Base64(samples: Float32Array): string {
  const buf = new ArrayBuffer(samples.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

/** RMS do bloco, 0..1 — alimenta a barrinha de nível, igual ao push-to-talk. */
export function rmsLevel(samples: Float32Array): number {
  let soma = 0;
  for (let i = 0; i < samples.length; i += 1) soma += samples[i] * samples[i];
  return Math.sqrt(soma / Math.max(1, samples.length));
}
